import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { HandwritingToolbarProps } from './types'

const PEN_COLORS = [
  { value: '#000000', label: '黑', className: 'bg-black' },
  { value: '#2563eb', label: '蓝', className: 'bg-blue-600' },
  { value: '#dc2626', label: '红', className: 'bg-red-600' },
]

const HIGHLIGHTER_COLORS = [
  { value: '#facc15', label: '黄', className: 'bg-yellow-400' },
  { value: '#60a5fa', label: '蓝', className: 'bg-blue-400' },
  { value: '#4ade80', label: '绿', className: 'bg-green-400' },
]

const PEN_WIDTHS = [
  { value: 2, label: '细' },
  { value: 4, label: '中' },
  { value: 8, label: '粗' },
]

export function HandwritingToolbar({
  tool,
  color,
  width,
  canUndo,
  isEmpty,
  onToolChange,
  onColorChange,
  onWidthChange,
  onUndo,
  onClear,
  onExport,
  showHighlighter = false,
  showExport = false,
  disabled = false,
}: HandwritingToolbarProps) {
  return (
    <div className="flex items-center gap-1 p-1.5 bg-white border border-slate-200 rounded-lg shadow-sm">
      {/* Pen colors */}
      <div className="flex items-center gap-0.5 px-1">
        {PEN_COLORS.map((c) => (
          <button
            key={c.value}
            className={cn(
              'w-6 h-6 rounded-full border-2 transition-all',
              c.className,
              tool === 'draw' && color === c.value
                ? 'border-slate-900 scale-110 ring-1 ring-slate-300'
                : 'border-transparent hover:border-slate-300'
            )}
            title={c.label}
            disabled={disabled}
            onClick={() => {
              onToolChange('draw')
              onColorChange(c.value)
            }}
          />
        ))}
      </div>

      {/* Highlighter (slides only) */}
      {showHighlighter && (
        <>
          <div className="w-px h-5 bg-slate-200" />
          <div className="flex items-center gap-0.5 px-1">
            {HIGHLIGHTER_COLORS.map((c) => (
              <button
                key={c.value}
                className={cn(
                  'w-6 h-6 rounded-full border-2 transition-all opacity-70',
                  c.className,
                  tool === 'draw' && color === c.value
                    ? 'border-slate-900 scale-110 ring-1 ring-slate-300'
                    : 'border-transparent hover:border-slate-300'
                )}
                title={`荧光笔-${c.label}`}
                disabled={disabled}
                onClick={() => {
                  onToolChange('draw')
                  onColorChange(c.value)
                }}
              />
            ))}
          </div>
        </>
      )}

      <div className="w-px h-5 bg-slate-200" />

      {/* Pen width */}
      <div className="flex items-center gap-0.5 px-1">
        {PEN_WIDTHS.map((w) => (
          <button
            key={w.value}
            className={cn(
              'w-6 h-6 flex items-center justify-center rounded text-xs transition-colors',
              width === w.value
                ? 'bg-slate-200 text-slate-900 font-medium'
                : 'text-slate-500 hover:bg-slate-100'
            )}
            title={w.label}
            disabled={disabled}
            onClick={() => onWidthChange(w.value)}
          >
            <div
              className="rounded-full bg-current"
              style={{ width: w.value + 2, height: w.value + 2 }}
            />
          </button>
        ))}
      </div>

      <div className="w-px h-5 bg-slate-200" />

      {/* Undo */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        title="撤销"
        disabled={disabled || !canUndo}
        onClick={onUndo}
      >
        ↩️
      </Button>

      {/* Clear */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        title="清空"
        disabled={disabled || isEmpty}
        onClick={onClear}
      >
        🗑️
      </Button>

      {/* Export (exam only) */}
      {showExport && onExport && (
        <>
          <div className="w-px h-5 bg-slate-200" />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            title="导出图片"
            disabled={disabled || isEmpty}
            onClick={onExport}
          >
            📤 导出
          </Button>
        </>
      )}
    </div>
  )
}
